import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { List, Map } from 'immutable';
import { injectIntl, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Modal, Button } from 'react-bootstrap';
import appConfig from '../../appConfig';
import PanelTree from '../PanelTree';
import { validateSessionConnection } from '../../actions/session';
import {
  getLabelFromAppConfig,
  showAuthWorkflowTemplate,
  showWorkflowDashTemplate,
  showMetaEditTool,
  showUsersAndRolesScreen,
  showManageCatRolesScreen,
  showNewRecordTool,
  showLoaderTool,
  showExportMetaTool,
} from '../../utils/uiUtils';
import { purgeRecyclebin } from '../../actions/categories';
import { showLocalAdminAsMain } from '../../actions/mainUI';
import {
  ADMIN_PUBLISH,
} from '../../constants/mainContentTypes';
import NotificationBoxIcon from '../Icons/NotificationBox';
import MetadataAttachIcon from '../Icons/MetadataAttach';
import WorkflowDashboardIcon from '../Icons/WorkflowDashboardDefault';
import UsersRolesIcon from '../Icons/UsersRolesDefault';
import AddNewRecordIcon from '../Icons/AddNewRecordDefault';
import RolesManagerIcon from '../Icons/RolesManagerDefult';
import PhotographyRequestIcon from '../Icons/PhotographyRequestDefault';
import RecycleBinIcon from '../Icons/RecycleBin';
import { primaryTextColor } from '../../themes/index';

const OPTION_AUTHWORKFLOW = 'AUTHWORKFLOW';
const OPTION_WORKFLOWDASH = 'WORKFLOWDASH';
const OPTION_METAATTACH = 'OPTION_METAATTACH';
const OPTION_USERSANDROLES = 'OPTION_USERSANDROLES';
const OPTION_MANAGECATEGORYROLES = 'OPTION_MANAGECATEGORYROLES';
const OPTION_PUBLISH = 'OPTION_PUBLISH';
const OPTION_CREATERELATIONAL = 'OPTION_CREATERELATIONAL';
const OPTION_MANAGE_LOADER = 'OPTION_MANAGE_LOADER';
const OPTION_IMPORT_METADATA = 'OPTION_IMPORT_METADATA';
const OPTION_RECYCLEBIN = 'OPTION_RECYCLEBIN';

const mapDispatchToProps = (dispatch) => ({
  dispatch: (actionToDispatch) => dispatch(actionToDispatch),
  validateSession: (surl) => dispatch(validateSessionConnection(surl)),
  showPublishAdminScreen: () => dispatch(showLocalAdminAsMain(ADMIN_PUBLISH)),
  emptyRecyclebin: (categoryID) => dispatch(purgeRecyclebin(categoryID)),
});

const mapStateToProps = (state) => ({
  session: state.session,
});

function NavAdminPanel({
  dispatch, session, validateSession, showPublishAdminScreen, emptyRecyclebin,
}) {
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const intl = useIntl();

  const optionsList = useMemo(() => {
    if (!appConfig.menuOptions?.adminMenu?.length) {
      return new List();
    }

    const optionConfigs = {
      auth: {
        i: OPTION_AUTHWORKFLOW,
        n: getLabelFromAppConfig(appConfig.templates.authoriseWorkflow, intl.locale),
        icon: <NotificationBoxIcon stroke={primaryTextColor} style={{ margin: '0 6px' }} />,
      },
      workflowDash: {
        i: OPTION_WORKFLOWDASH,
        n: getLabelFromAppConfig(appConfig.templates.workflowDashboard, intl.locale),
        icon: <WorkflowDashboardIcon stroke={primaryTextColor} style={{ margin: '0 6px' }} />,
      },
      metaAttach: {
        i: OPTION_METAATTACH,
        n: intl.formatMessage({ id: 'navAdminPanel.metaAttach' }),
        icon: <MetadataAttachIcon stroke={primaryTextColor} style={{ margin: '0 6px' }} />,
      },
      usersAndRoles: {
        i: OPTION_USERSANDROLES,
        n: intl.formatMessage({ id: 'navAdminPanel.usersAndRoles' }),
        icon: <UsersRolesIcon fill={primaryTextColor} style={{ margin: '0 6px' }} />,
      },
      manageCategoryRoles: {
        i: OPTION_MANAGECATEGORYROLES,
        n: intl.formatMessage({ id: 'navAdminPanel.manageCategoryRoles' }),
        icon: <RolesManagerIcon fill={primaryTextColor} style={{ margin: '0 6px' }} />,
      },
      publish: {
        i: OPTION_PUBLISH,
        n: intl.formatMessage({ id: 'navAdminPanel.publishBtn' }),
        icon: <PhotographyRequestIcon fill={primaryTextColor} style={{ margin: '0 6px' }} />,
      },
      newRelational: {
        i: OPTION_CREATERELATIONAL,
        n: intl.formatMessage({ id: 'navAdminPanel.createRelationalBtn' }),
        icon: <AddNewRecordIcon width={14} height={14} stroke={primaryTextColor} style={{ margin: '0 6px' }} />,
      },
      loader: {
        i: OPTION_MANAGE_LOADER,
        n: intl.formatMessage({ id: 'navAdminPanel.manageLoader' }),
        icon: <NotificationBoxIcon stroke={primaryTextColor} style={{ margin: '0 6px' }} />,
      },
      importMetadata: {
        i: OPTION_IMPORT_METADATA,
        n: intl.formatMessage({ id: 'navAdminPanel.importMetadata' }),
        icon: <MetadataAttachIcon stroke={primaryTextColor} style={{ margin: '0 6px' }} />,
      },
      recycleBin: {
        i: OPTION_RECYCLEBIN,
        n: intl.formatMessage({ id: 'navAdminPanel.recycleBin' }),
        icon: <RecycleBinIcon stroke={primaryTextColor} style={{ margin: '0 6px' }} />,
      },
    };

    const options = appConfig.menuOptions.adminMenu
      .filter(menuOption => optionConfigs[menuOption])
      .map(menuOption => new Map(optionConfigs[menuOption]));

    return new List(options);
  }, [intl]);

  const handlePurgeConfirm = () => {
    emptyRecyclebin();
    setShowPurgeConfirm(false);
  };

  const purgeConfirmationModal = (
    <Modal
      show={showPurgeConfirm}
      onHide={() => setShowPurgeConfirm(false)}
      centered
      className="umo-toolbar__purge-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({ id: 'adminPurge.purgeConfirmationTitle' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {intl.formatMessage({ id: 'adminPurge.purgeConfirmMessage' })}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={() => setShowPurgeConfirm(false)}>
          {intl.formatMessage({ id: 'general.cancel' })}
        </Button>
        <Button variant="primary" onClick={handlePurgeConfirm}>
          {intl.formatMessage({ id: 'adminPurge.purgeConfirmationButton' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const onPanelItemClick = (key) => {
    switch (key) {
      case OPTION_AUTHWORKFLOW:
        showAuthWorkflowTemplate(dispatch, session.get('locale'));
        break;
      case OPTION_METAATTACH:
        showMetaEditTool(null, dispatch);
        break;
      case OPTION_WORKFLOWDASH:
        showWorkflowDashTemplate(dispatch, session.get('locale'));
        break;
      case OPTION_USERSANDROLES:
        showUsersAndRolesScreen(dispatch, session.get('locale'));
        break;
      case OPTION_MANAGECATEGORYROLES:
        showManageCatRolesScreen(dispatch, appConfig.restrictEditing);
        break;
      case OPTION_PUBLISH:
        showPublishAdminScreen();
        break;
      case OPTION_CREATERELATIONAL:
        showNewRecordTool(dispatch);
        break;
      case OPTION_MANAGE_LOADER:
        showLoaderTool(dispatch);
        break;
      case OPTION_IMPORT_METADATA:
        showExportMetaTool(dispatch);
        break;
      case OPTION_RECYCLEBIN:
        setShowPurgeConfirm(true);
        break;
      default: break;
    }
  };

  return (
    <div className="nav-content__body">
      <h2 className="nav-content__title">{intl.formatMessage({ id: 'navAdminPanel.adminHeader' })}</h2>
      <PanelTree
        onPanelItemClick={onPanelItemClick}
        panelTreeList={optionsList}
        isRoot
      />
      {purgeConfirmationModal}
    </div>
  );
}

NavAdminPanel.propTypes = {
  dispatch: PropTypes.func,
  session: PropTypes.instanceOf(Map),
  validateSession: PropTypes.func,
  showPublishAdminScreen: PropTypes.func,
  emptyRecyclebin: PropTypes.func,
};

const MemoizedNavAdminPanel = React.memo(NavAdminPanel);

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(MemoizedNavAdminPanel));
